import { useState, useEffect } from 'react';
import { Bell, CheckCircle, Info, AlertCircle, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface Notification {
  id: string;
  type: 'success' | 'info' | 'warning';
  title: string;
  message: string;
  read: boolean;
  created_at: string;
}

export default function Notifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotifications();
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
    } catch (error) {
      console.error('加载通知失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(notifications.map(n => n.id === id ? { ...n, read: true } : n));
    } catch (error) {
      console.error('标记已读失败:', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(notifications.filter(n => n.id !== id));
    } catch (error) {
      alert('删除失败');
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (error) {
      console.error('标记全部已读失败:', error);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-6 h-6 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-400" />;
      default:
        return <Info className="w-6 h-6 text-[#22a9d9]" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <div className="flex items-center justify-between mb-2">
          <h1 className="text-3xl font-bold text-white">通知</h1>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="text-[#22a9d9] text-sm hover:text-[#22a9d9]/80 transition-colors"
            >
              全部已读
            </button>
          )}
        </div>
        <p className="text-white/60">
          {unreadCount > 0 ? `${unreadCount} 条未读` : '没有未读通知'}
        </p>
      </div>

      {/* 通知列表 */}
      <div className="px-6 space-y-3">
        {loading ? (
          <div className="text-center py-12 text-white/60">
            加载中...
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60">暂无通知</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              className={`glass rounded-2xl p-4 ${!notification.read ? 'border-l-4 border-[#22a9d9]' : ''}`}
              onClick={() => !notification.read && handleMarkRead(notification.id)}
            >
              <div className="flex gap-4">
                {/* 图标 */}
                <div className="flex-shrink-0 mt-1">
                  {getIcon(notification.type)}
                </div>

                {/* 内容 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="text-white font-medium flex-1">
                      {notification.title}
                      {!notification.read && (
                        <span className="ml-2 inline-block w-2 h-2 bg-[#22a9d9] rounded-full" />
                      )}
                    </h3>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(notification.id);
                      }}
                      className="text-white/40 hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-white/70 text-sm mb-2">
                    {notification.message}
                  </p>
                  <span className="text-white/40 text-xs">
                    {new Date(notification.created_at).toLocaleString()}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <BottomNav />
    </div>
  );
}
